import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import IntelAxisTablePagination from '../../../components/mi/IntelAxisTablePagination';
import { getPrNewsTableOptions, listPrNewsTable } from '../../../api/nexus';
import { assignFilterArray } from '../../../utils/filtersApi';
import MiTableFiltersDrawer from '../../../components/mi/MiTableFiltersDrawer';
import TableHoverTooltip from '../../../components/mi/TableHoverTooltip';
import CompanyBrandCell from '../../../components/CompanyBrandCell';
import NewsPrDrawerCard from '../components/NewsPrDrawerCard';
import { formatTableDate } from '../../../utils/intelAxisTableUtils';

function NewsPrTableImage({ imageURL, headline }) {
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    setFailed(false);
  }, [imageURL]);

  if (!imageURL || failed) {
    return <div className="news-pr-table-thumb news-pr-table-thumb--empty" aria-hidden="true" />;
  }

  return (
    <div className="news-pr-table-thumb">
      <img
        className="news-pr-table-thumb__img"
        src={imageURL}
        alt={headline ? `Image: ${headline}` : 'Article image'}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

function NewsPrTableCell({ value, setTooltip }) {
  if (!value || value === '—') return '—';
  const str = String(value);
  return (
    <span
      className="news-pr-table-cell__text"
      onMouseMove={(e) => setTooltip({ x: e.clientX, y: e.clientY, content: str })}
      onMouseLeave={() => setTooltip(null)}
    >
      {str}
    </span>
  );
}

export default function NewsPrTable() {
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCompany, setFilterCompany] = useState('All companies');
  const [showTableFilters, setShowTableFilters] = useState(false);
  const [options, setOptions] = useState({ companies: ['All companies'] });
  const [selectedId, setSelectedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pageInput, setPageInput] = useState('1');
  const [openFilter, setOpenFilter] = useState(null);
  const [mapTooltip, setMapTooltip] = useState(null);

  const selectedRow = useMemo(
    () => rows.find((r) => r.id === selectedId) || null,
    [rows, selectedId],
  );

  const companyCatalog = useMemo(
    () => options.companies.filter((name) => name && name !== 'All companies'),
    [options.companies],
  );

  useEffect(() => {
    getPrNewsTableOptions()
      .then((res) => {
        const data = res?.data?.data || {};
        setOptions({ companies: data.companies || ['All companies'] });
      })
      .catch((err) => console.error('[news-pr] options failed', err));
  }, []);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const body = {
      page: currentPage,
      limit: rowsPerPage,
      ...(searchTerm ? { search: searchTerm } : {}),
    };
    assignFilterArray(body, 'company', filterCompany, 'All companies', companyCatalog);

    const timer = setTimeout(() => {
      listPrNewsTable(body)
        .then((res) => {
          if (cancelled) return;
          setRows(res?.data?.data || []);
          setTotalRows(res?.data?.meta?.totalItems || 0);
        })
        .catch((err) => {
          console.error('[news-pr] list failed', err);
          if (!cancelled) {
            setRows([]);
            setTotalRows(0);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [currentPage, rowsPerPage, searchTerm, filterCompany, companyCatalog]);

  const hasFiltersApplied = filterCompany !== 'All companies' || Boolean(searchTerm.trim());

  const openArticleView = (row, event) => {
    event?.stopPropagation?.();
    if (!row?.id) return;
    sessionStorage.setItem('comply_last_news_pr_path', location.pathname + location.search);
    navigate(`/news-pr/${row.id}`);
  };

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const next = parseInt(pageInput, 10);
    const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;
    if (Number.isFinite(next) && next >= 1 && next <= totalPages) {
      setCurrentPage(next);
    } else {
      setPageInput(String(currentPage));
    }
  };

  if (loading && rows.length === 0) {
    return <div className="content">Loading news & PR…</div>;
  }

  return (
    <>
      <PageHeader
        title="News / PR"
        activePageTab="Table"
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setCurrentPage(1);
        }}
        placeholder="Search headline, company, or summary…"
        hasFiltersApplied={hasFiltersApplied}
        onClearFilters={() => {
          setSearchTerm('');
          setFilterCompany('All companies');
          setCurrentPage(1);
        }}
        showTableFilters={showTableFilters}
        onToggleFilters={() => setShowTableFilters(!showTableFilters)}
      />

      <div className="body">
        <div className="table-area filings-table-panel news-pr-table-panel">
          <div className="table-wrap">
            <table>
              <colgroup>
                <col className="news-pr-col-date" />
                <col className="news-pr-col-company" />
                <col className="news-pr-col-headline" />
                <col className="news-pr-col-image" />
                <col className="news-pr-col-summary" />
                <col className="news-pr-col-source" />
              </colgroup>
              <thead>
                <tr>
                  <th className="col-date"><div className="th-sort">Date</div></th>
                  <th className="col-company"><div className="th-sort">Company</div></th>
                  <th className="col-product-name">Headline</th>
                  <th className="col-preview">Image</th>
                  <th className="col-type">Summary</th>
                  <th className="col-status">Visit</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: 'var(--text3)' }}>
                      No news / PR items match your filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, rowIdx) => (
                    <tr
                      key={row.id}
                      className={selectedId === row.id ? 'selected' : ''}
                      style={{ '--table-row-delay': rowIdx }}
                      onClick={() => setSelectedId(row.id)}
                      onDoubleClick={(e) => openArticleView(row, e)}
                    >
                      <td className="date-cell">{formatTableDate(row.dateOfNews)}</td>
                      <td className="company-cell">
                        <div className="sec-filing-table__company-name">
                          <CompanyBrandCell companyName={row.company} />
                        </div>
                      </td>
                      <td className="news-pr-table-cell">
                        <NewsPrTableCell value={row.headline} setTooltip={setMapTooltip} />
                      </td>
                      <td className="news-pr-preview-cell">
                        <NewsPrTableImage imageURL={row.imageURL} headline={row.headline} />
                      </td>
                      <td className="news-pr-table-cell">
                        <NewsPrTableCell value={row.introText} setTooltip={setMapTooltip} />
                      </td>
                      <td className="news-pr-source-cell">
                        {row.landingPageUrl ? (
                          <a
                            href={row.landingPageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="table-drawer-action-btn news-pr-source-link"
                            aria-label="Visit article"
                            title="Visit article"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink size={16} strokeWidth={1.75} aria-hidden />
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <IntelAxisTablePagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalRows={totalRows}
            entityLabel="articles"
            pageInput={pageInput}
            setPageInput={setPageInput}
            onPageInputSubmit={handlePageInputSubmit}
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            filterKey="news-rows"
          />
        </div>

        <div className={`drawer ${selectedId ? 'drawer-open' : ''}`}>
          {selectedRow ? (
            <>
              <div className="drawer-header">
                <div>
                  <div className="drawer-company">{selectedRow.company}</div>
                  <div className="drawer-toi">{formatTableDate(selectedRow.dateOfNews)}</div>
                </div>
                <button
                  type="button"
                  className="drawer-close filters-drawer-close"
                  onClick={() => setSelectedId(null)}
                >
                  ×
                </button>
              </div>
              <div className="drawer-body">
                <NewsPrDrawerCard row={selectedRow} />
                <div className="detail-section">
                  <div className="ds-title">Activity</div>
                  <div className="field-grid">
                    <div className="field">
                      <div className="field-label">Date of news</div>
                      <div className="field-val">{formatTableDate(selectedRow.dateOfNews)}</div>
                    </div>
                    <div className="field">
                      <div className="field-label">Date added</div>
                      <div className="field-val">{formatTableDate(selectedRow.dateAdded)}</div>
                    </div>
                    <div className="field">
                      <div className="field-label">Ingested</div>
                      <div className="field-val">{formatTableDate(selectedRow.ingestedAt)}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="drawer-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-filters"
                  onClick={(e) => openArticleView(selectedRow, e)}
                >
                  Read News
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <TableHoverTooltip tooltip={mapTooltip} />

      <MiTableFiltersDrawer
        isOpen={showTableFilters}
        onClose={() => setShowTableFilters(false)}
        filterCompany={filterCompany}
        setFilterCompany={setFilterCompany}
        companyCatalog={companyCatalog}
        setCurrentPage={setCurrentPage}
      />
    </>
  );
}
